/**
 * Converts a date into YOM-friendly formatting.
 * @param {Date} date The date to format.
 * @param {"dateTime"|"date"} [format="dateTime"] Whether to include the time of day.
 * @returns {string} formatted date string.
 */
const formatDate = (date, format = "dateTime") => {
  const options = {
    year: "numeric",
    month: "short",
    day: "2-digit",
  };

  if (format === "dateTime") {
    options.hourCycle = "h12";
    options.hour = "2-digit";
    options.minute = "2-digit";
    options.second = "2-digit";
  }

  const dateFormatter = Intl.DateTimeFormat("en-US", options);
  const formattedDate = dateFormatter.format(date);

  return formattedDate.replace(/,/g, "");
};

/**
 * Parses a UNIX timestamp, date/datetime string, or Date object into a Date,
 * treating timezone-less strings as UTC.
 * @param {number|string|Date} input A UNIX timestamp in milliseconds or seconds, a date/datetime string, or a Date object
 * @returns {Date|null} parsed Date, or null if input is missing/invalid
 */
export const parseDateUtc = (input) => {
  if (!input) return null;

  let date;
  if (typeof input === "number" || /^\d+$/.test(input)) {
    const timestampStr = input.toString();

    if (timestampStr.length === 13) {
      date = new Date(Number(input));
    } else if (timestampStr.length === 10) {
      date = new Date(Number(input) * 1000);
    } else {
      return null;
    }
  } else if (typeof input === "string") {
    const dateString = input.trim();

    // Strings with explicit timezone info are already unambiguous - let the
    // native parser handle them.
    const hasTimezoneInfo =
      /Z$/i.test(dateString) ||
      /[+-]\d{2}:?\d{2}$/.test(dateString) ||
      /utc|gmt/i.test(dateString);

    if (hasTimezoneInfo) {
      date = new Date(dateString);
    } else {
      // No timezone info: pull the numeric components out ourselves and
      // build the Date via Date.UTC instead of handing a massaged string to
      // the native parser. Date.UTC is fully spec-defined arithmetic, so the
      // result can't vary by JS engine/Node build the way string parsing can.
      const match = dateString.match(
        /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?)?$/
      );

      if (match) {
        const [
          ,
          year,
          month,
          day,
          hour = "0",
          minute = "0",
          second = "0",
          ms = "0",
        ] = match;
        date = new Date(
          Date.UTC(
            Number(year),
            Number(month) - 1,
            Number(day),
            Number(hour),
            Number(minute),
            Number(second),
            Number(ms.padEnd(3, "0").slice(0, 3))
          )
        );
      } else {
        date = new Date(dateString);
      }
    }
  } else if (input instanceof Date) {
    date = input;
  } else {
    return null;
  }

  return isNaN(date.getTime()) ? null : date;
};

/**
 * Render function that formats dates for datatable columns.
 * @param {number|string|Date} input A UNIX timestamp in milliseconds or seconds, a date/datetime string, or a Date object
 * @param {"dateTime"|"date"} [format="dateTime"] Whether to include the time of day.
 * @returns {string} formatted date string
 */
export const renderDate = (input, format = "dateTime") => {
  const date = parseDateUtc(input);
  return date ? formatDate(date, format) : null;
};
